import { useEffect, useMemo, useState } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import PropertyHero from './components/PropertyHero';
import { GalleryGrid, GalleryLightbox } from './components/PropertyGallery';
import PropertyMatterport from './components/PropertyMatterport';
import PropertyStory from './components/PropertyStory';
import SignatureFeatures from './components/SignatureFeatures';
import EssentialInfo from './components/EssentialInfo';
import PropertyConcierge from './components/PropertyConcierge';
import PropertyReviews from './components/PropertyReviews';
import StickyBookBar from './components/StickyBookBar';
import OwnerRezWidgets from './components/OwnerRezWidgets';
import { ownerRezWidgetsForProperty } from '@/mocks/ownerRezWidgets';
import { conciergeConfig, reviews } from './data/belvedere';
import type {
  EssentialInfoGroup,
  GalleryImage,
  PropertyHeroData,
  PropertyStoryData,
  SignatureFeature,
} from './types';

type OwnerRezProofSnapshot = {
  generated_at: string;
  source: string;
  property: {
    id: number;
    name: string;
    city: string;
    state: string;
    street1: string;
    postal_code: string;
    bedrooms: number;
    bathrooms: number;
    bathrooms_full: number;
    bathrooms_half: number;
    max_guests: number;
    max_pets?: number;
    living_area?: number;
    living_area_type?: string;
    check_in?: string;
    check_out?: string;
    thumbnail_url_large?: string;
  };
  listing: {
    headline?: string;
    description?: string;
    location_types?: string[];
    photos: { src: string; original?: string; caption?: string }[];
    categories: Record<string, string[]>;
  };
};

const snapshotUrl = '/ownerrez-poc/belvedere.json';

function cleanText(value: string) {
  return value
    .replace(/&ndash;/g, '-')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function firstSentences(text: string, count: number) {
  return cleanText(text)
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean)
    .slice(0, count)
    .join(' ');
}

function sectionFromDescription(description: string, heading: string) {
  const nextHeading = /\n(?:Level \d|Location|The Belvedere: Property Features|Property Description)\n/g;
  const start = description.indexOf(`\n${heading}\n`);
  if (start === -1) return '';

  const fromHeading = description.slice(start + heading.length + 2);
  nextHeading.lastIndex = 0;
  const next = nextHeading.exec(fromHeading);
  return cleanText(next ? fromHeading.slice(0, next.index) : fromHeading);
}

function findPhoto(photos: GalleryImage[], index: number) {
  return photos[index]?.src ?? photos[0]?.src ?? '';
}

function category(snapshot: OwnerRezProofSnapshot, key: string) {
  return (snapshot.listing.categories[key] ?? []).map(cleanText).filter(Boolean);
}

function makeEssentialGroups(snapshot: OwnerRezProofSnapshot): EssentialInfoGroup[] {
  const { property, listing } = snapshot;
  const bathrooms = category(snapshot, 'Bathrooms');
  const bedrooms = category(snapshot, 'Bedrooms');
  const houseRules = category(snapshot, 'HouseRules');

  return [
    {
      heading: 'Source details at a glance',
      items: [
        `${property.bedrooms} bedrooms`,
        `${property.bathrooms} bathrooms (${property.bathrooms_full} full, ${property.bathrooms_half} half)`,
        `Sleeps up to ${property.max_guests} guests`,
        property.living_area ? `${property.living_area.toLocaleString()} ${property.living_area_type}` : '',
        `${property.city}, ${property.state}`,
        property.max_pets ? 'Pet policy appears in the source data and should be verified before publishing' : '',
      ].filter(Boolean),
    },
    {
      heading: 'Setting & view',
      items: category(snapshot, 'LocationTypeFeatures'),
    },
    {
      heading: 'House rules',
      items: houseRules.slice(0, 8),
    },
    {
      heading: 'Bedrooms',
      items: bedrooms.slice(0, 8),
    },
    {
      heading: 'Bathrooms',
      items: bathrooms.slice(0, 8),
    },
    {
      heading: 'Popular amenities',
      items: category(snapshot, 'PopularAmenityFeatures').slice(0, 10),
    },
    {
      heading: 'Kitchen',
      items: category(snapshot, 'KitchenFeatures').slice(0, 10),
    },
    {
      heading: 'Outdoor',
      items: category(snapshot, 'OutdoorFeatures').slice(0, 10),
    },
    {
      heading: 'Parking',
      items: category(snapshot, 'ParkingFeatures'),
    },
  ].filter((group) => group.items.length > 0);
}

function makeGallery(snapshot: OwnerRezProofSnapshot): GalleryImage[] {
  return snapshot.listing.photos.map((photo, index) => ({
    src: photo.src,
    alt: photo.caption || `${snapshot.property.name} source photo ${index + 1}`,
    caption: photo.caption || `Source photo ${index + 1} of ${snapshot.listing.photos.length}`,
  }));
}

function makeHero(snapshot: OwnerRezProofSnapshot, gallery: GalleryImage[]): PropertyHeroData {
  return {
    id: 'belvedere',
    name: snapshot.property.name,
    location: snapshot.property.city,
    area: '30A',
    bedrooms: String(snapshot.property.bedrooms),
    bathrooms: String(snapshot.property.bathrooms),
    sleeps: `Up to ${snapshot.property.max_guests}`,
    heroImage: gallery[0]?.src ?? snapshot.property.thumbnail_url_large ?? '',
    videoUrl: 'https://player.vimeo.com/video/772057726?autoplay=1&title=0&portrait=0&muted=0&loop=0',
    matterportUrl: 'https://my.matterport.com/show/?m=F12AZbdUQZS&mls=1',
  };
}

function makeStory(snapshot: OwnerRezProofSnapshot): PropertyStoryData {
  const description = snapshot.listing.description ?? '';
  const level1 = sectionFromDescription(description, 'Level 1');
  const level2 = sectionFromDescription(description, 'Level 2');
  const level3 = sectionFromDescription(description, 'Level 3');
  const level4 = sectionFromDescription(description, 'Level 4');
  const level5 = sectionFromDescription(description, 'Level 5');
  const location = sectionFromDescription(description, 'Location');

  return {
    heading: snapshot.property.name,
    subheading: snapshot.listing.headline,
    paragraphs: [
      firstSentences(description, 3),
      level1 ? firstSentences(level1, 3) : '',
      level2 ? firstSentences(level2, 4) : '',
      [level3, level4].filter(Boolean).map((section) => firstSentences(section, 2)).join(' '),
      level5 ? firstSentences(level5, 3) : '',
    ].filter(Boolean),
    locationContext: location || `${snapshot.property.name} is located in ${snapshot.property.city}, ${snapshot.property.state}.`,
    address: snapshot.property.street1,
    addressSub: `${snapshot.property.city}, ${snapshot.property.state} ${snapshot.property.postal_code}`,
    propertyMeta: `${snapshot.property.bedrooms} bedrooms - ${snapshot.property.bathrooms} bathrooms - sleeps up to ${snapshot.property.max_guests}`,
  };
}

function makeSignatureFeatures(snapshot: OwnerRezProofSnapshot, gallery: GalleryImage[]): SignatureFeature[] {
  const description = snapshot.listing.description ?? '';
  const level1 = sectionFromDescription(description, 'Level 1');
  const level2 = sectionFromDescription(description, 'Level 2');
  const level5 = sectionFromDescription(description, 'Level 5');
  const location = sectionFromDescription(description, 'Location');

  return [
    {
      title: 'Private Elevator',
      body: firstSentences(level1, 3),
      image: findPhoto(gallery, 1),
      imageAlt: `${snapshot.property.name} source photo`,
      tag: 'Description detail',
    },
    {
      title: 'Level 2 Kitchen & Pool',
      body: firstSentences(level2, 5),
      image: findPhoto(gallery, 5),
      imageAlt: `${snapshot.property.name} kitchen and pool details`,
      tag: 'Description detail',
    },
    {
      title: 'Rooftop Outdoor Living',
      body: firstSentences(level5, 4),
      image: findPhoto(gallery, 8),
      imageAlt: `${snapshot.property.name} rooftop details`,
      tag: 'Description detail',
    },
    {
      title: 'Rosemary Beach Location',
      body: firstSentences(location, 4),
      image: findPhoto(gallery, 10),
      imageAlt: `${snapshot.property.name} location details`,
      tag: 'Description detail',
    },
  ].filter((feature) => feature.body && feature.image);
}

function ProofBanner({ generatedAt }: { generatedAt: string }) {
  return (
    <section className="bg-foreground-950 text-background-50 border-y border-background-50/10 py-4">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
        <p className="text-[11px] uppercase tracking-[0.22em] text-secondary-200">
          Source content preview
        </p>
        <p className="text-sm text-background-100/75">
          Content below is rendered from a saved Belvedere data sample refreshed {new Date(generatedAt).toLocaleString()}.
        </p>
      </div>
    </section>
  );
}

export default function OwnerRezBelvedereProofPage() {
  const [snapshot, setSnapshot] = useState<OwnerRezProofSnapshot | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const ownerRezWidgets = ownerRezWidgetsForProperty('belvedere');
  const reviewsWidget = ownerRezWidgets.find((widget) => widget.kind === 'reviews');

  useEffect(() => {
    fetch(snapshotUrl)
      .then((response) => response.json())
      .then(setSnapshot)
      .catch(() => setSnapshot(null));
  }, []);

  const pageData = useMemo(() => {
    if (!snapshot) return null;
    const gallery = makeGallery(snapshot);
    return {
      gallery,
      hero: makeHero(snapshot, gallery),
      story: makeStory(snapshot),
      features: makeSignatureFeatures(snapshot, gallery),
      groups: makeEssentialGroups(snapshot),
    };
  }, [snapshot]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!snapshot || !pageData) {
    return (
      <main className="min-h-screen bg-background-50 text-foreground-900 font-body">
        <Header />
        <section className="pt-40 pb-24 px-5 md:px-10 max-w-[900px] mx-auto">
          <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700 mb-4">Source content preview</p>
          <h1 className="font-heading text-5xl text-foreground-950">Loading Belvedere preview...</h1>
          <p className="mt-5 text-foreground-600">
            This hidden page reads a local data sample for the Belvedere.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />
      <PropertyHero data={pageData.hero} onViewPhotos={() => openLightbox(0)} />
      <ProofBanner generatedAt={snapshot.generated_at} />
      <GalleryGrid images={pageData.gallery} propertyName={pageData.hero.name} onRequestOpen={openLightbox} />
      <PropertyMatterport propertyName={pageData.hero.name} src={pageData.hero.matterportUrl} />
      <EssentialInfo
        groups={pageData.groups}
        heading={{ lead: 'Source details,', accent: 'inside our format.' }}
        footnote="This page is a proof of concept. The raw property content is shown inside the website layout so we can compare source data with a more curated presentation."
      />
      <PropertyStory data={pageData.story} propertyName={pageData.hero.name} />
      <SignatureFeatures features={pageData.features} />
      <OwnerRezWidgets propertyName={pageData.hero.name} widgets={ownerRezWidgets} />
      <PropertyReviews reviews={reviews} propertyName={pageData.hero.name} ownerRezWidget={reviewsWidget} />
      <PropertyConcierge config={{ ...conciergeConfig, propertyName: pageData.hero.name }} />
      <Footer />
      <StickyBookBar
        data={{
          propertyId: 'belvedere',
          propertyName: pageData.hero.name,
          location: `${snapshot.property.city} - 30A`,
          bedrooms: `${snapshot.property.bedrooms} bed`,
          sleeps: `Sleeps ${snapshot.property.max_guests}`,
        }}
      />
      <GalleryLightbox
        images={pageData.gallery}
        isOpen={lightboxOpen}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </main>
  );
}
